import type { QueryClient } from "@tanstack/react-query";

/**
 * Invalidate cached queries that touch a given briefing.
 *
 * Targeted in two dimensions:
 *
 * 1. Caller can pass `knownData` for keys whose new value we already
 *    have in hand (the briefing UPDATE return, the briefing_stages
 *    upsert return, etc.). Those go through `setQueryData` and skip
 *    the refetch entirely — saving 1 round-trip per known key.
 *
 * 2. The remaining invalidation runs with `refetchType: "active"`
 *    (the TanStack default, made explicit here): only currently
 *    mounted queries refetch immediately. Inactive ones (queries
 *    used by other pages that aren't on screen) are marked stale
 *    and refetch lazily when their page next mounts. This stops
 *    "save fan-out" from triggering ~15 parallel refetches when
 *    only the editor is visible.
 *
 * Predicate covers all briefing-id-keyed queries plus a small set
 * of always-affected global keys.
 *
 * Awaits the refetch so save-and-navigate flows mount on fresh
 * data — the cache invalidation must settle before the new route
 * reads its queries.
 */
export type KnownBriefingData = {
  /** Result of `briefings UPDATE … RETURNING *` — populates ["briefing", id] + ["briefing-view", id]. */
  briefing?: unknown;
  /** Latest briefing_stages rows — populates ["briefing_stages", id], ["briefing_stages_editor", id], etc. */
  briefingStages?: unknown[];
};

export async function invalidateBriefingCaches(
  queryClient: QueryClient,
  briefingId: string,
  knownData: KnownBriefingData = {},
): Promise<void> {
  // Optimistic cache writes for keys we already have authoritative
  // data for. `setQueryData` updates `dataUpdatedAt` so the cache
  // entry is fresh — invalidateQueries below skips it.
  const populated = new Set<string>();
  if (knownData.briefing !== undefined) {
    queryClient.setQueryData(["briefing", briefingId], knownData.briefing);
    queryClient.setQueryData(["briefing-view", briefingId], knownData.briefing);
    populated.add("briefing");
    populated.add("briefing-view");
  }
  if (knownData.briefingStages !== undefined) {
    queryClient.setQueryData(["briefing_stages", briefingId], knownData.briefingStages);
    queryClient.setQueryData(["briefing_stages_editor", briefingId], knownData.briefingStages);
    queryClient.setQueryData(["briefing_stages_setup", briefingId], knownData.briefingStages);
    queryClient.setQueryData(["briefing_stages_crew", briefingId], knownData.briefingStages);
    populated.add("briefing_stages");
    populated.add("briefing_stages_editor");
    populated.add("briefing_stages_setup");
    populated.add("briefing_stages_crew");
  }

  await queryClient.invalidateQueries({
    refetchType: "active",
    predicate: (query) => {
      const key = query.queryKey;
      if (!Array.isArray(key) || key.length === 0) return false;
      const first = String(key[0] ?? "");
      const second = key[1];

      // Skip keys we just wrote authoritative data for.
      if (populated.has(first) && second === briefingId) return false;

      // Anything keyed on this briefing's id, in any prefix.
      if (typeof second === "string" && second === briefingId) return true;
      // Globally affected keys that don't carry an id.
      const globalKeys = new Set([
        "briefings",
        "crew_assignments",
        "all-crew-members",
        "crew-members",
        "assignments",
      ]);
      return globalKeys.has(first);
    },
  });
}
